import { observable, computed, decorate } from 'mobx';
import { integrationsManifests } from '../api/integrations-manifests';
import api from '../api';

class IntegrationsStore {
  constructor({ config, pubSub }) {
    this.pubSub = pubSub;
    this.config = config;

    this.integrationsDetails = new Map();
    this.integrationsReady = false;
    this._initializeIntegrations(config);
    this.integrationsReady = true;
  }

  _initializeIntegrations = (config) => {
    config.widgets.forEach((integration) => {
      if (integrationsManifests[integration.key]) {
        const integrationData = integrationsManifests[integration.key](
          config.experiments,
        );
        integrationData.isPermitted = this._isPermitted({
          userPermissions: config.userPermissions,
          integrationPermissions: integrationData.requiredPermissions,
        });
        this.integrationsDetails.set(integration.key, integrationData);
      }
    });
  };

  getWidgetConfig = (key) => {
    try {
      const integrationData = this.integrationsDetails.get(key);
      return {
        ...integrationData.basic,
        isPermitted: integrationData.isPermitted,
      };
    } catch (ex) {
      console.error(
        `Error while loading integration basic config for key '${key}': `,
        ex,
      );
    }
  };

  _isPermitted = ({ userPermissions, integrationPermissions }) => {
    return (
      !integrationPermissions ||
      !userPermissions ||
      integrationPermissions.every((permission) =>
        userPermissions.includes(permission),
      )
    );
  };

  getStatus = (key) => {
    const extractedDetails = this.getExtractedDetails(key);
    return extractedDetails && extractedDetails.status;
  };

  setProperty = (key, property, value) => {
    const integrationDetails = this.integrationsDetails.get(key);
    if (integrationDetails && integrationDetails.extractedDetails) {
      const integration = observable({ ...integrationDetails });
      integration.extractedDetails[property] = value;
      this.integrationsDetails.set(key, integration);
    }
  };

  setDetailedConfig = (key) => {
    try {
      return new Promise((resolve) => {
        const integrationApi = api(key, this.config, this.pubSub);
        const integrationConfig = {
          apiBaseUrl: this.config.apiBaseUrl,
          experiments: this.config.experiments,
          locale: this.config.locale,
        };
        return this.integrationsDetails
          .get(key)
          .detailed(integrationApi, integrationConfig)
          .then((extractedDetails) => {
            const integrationData = this.integrationsDetails.get(key);
            integrationData.extractedDetails = extractedDetails;
            this.integrationsDetails.set(
              key,
              observable({ ...integrationData }),
            );
            resolve(extractedDetails);
          });
      });
    } catch (ex) {
      console.error(
        `Error while loading integration detailed config for key '${key}': `,
        ex,
      );
    }
  };

  getExtractedDetails = (key) => {
    const integrationDetails = this.integrationsDetails.get(key);
    return integrationDetails && integrationDetails.extractedDetails;
  };

  get areIntegrationsReady() {
    return this.integrationsReady;
  }
}

export default decorate(IntegrationsStore, {
  integrationsDetails: observable,
  integrationsReady: observable,
  areIntegrationsReady: computed,
});
